/*
 * Created on Wed Nov 01 2023 4:27:51 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */
import React from "react";
import { ArrowRight, Sparkles, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getIcon } from "../utils/iconResolver";
import data from "../data/krumagoData.json";

const ParentCTA = () => {
  const { cta } = data;
  const nav = useNavigate();

  return (
    <section className="relative py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-100 overflow-hidden">
      <div className="relative max-w-7xl mx-auto">
        <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 sm:p-12 lg:p-16 text-white overflow-hidden shadow-3xl">

          {/* Dot pattern */}
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
                backgroundSize: "40px 40px",
              }}
            />
          </div>

          {/* Glow orbs */}
          <div className="absolute top-0 right-0 w-48 h-48 sm:w-64 sm:h-64 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-48 h-48 sm:w-64 sm:h-64 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="relative text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 text-white font-semibold px-4 sm:px-5 py-2 sm:py-2.5 rounded-full border border-white/20 mb-8 sm:mb-10">
              <Sparkles className="w-4 h-4" />
              <span className="text-xs sm:text-sm">{cta.badge}</span>
            </div>

            {/* Headline */}
            <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4 sm:mb-6 leading-tight">
              {cta.headline.main}{" "}
              <span className="font-serif font-black">
                with{" "}
                <span className="text-[rgb(0,0,168)]">Kru</span>
                <span className="text-[rgb(227,6,19)]">Ma</span>
                <span className="text-[rgb(0,0,168)]">go</span>
              </span>
              ?
            </h3>

            <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-10 sm:mb-12 max-w-3xl mx-auto leading-relaxed font-light">
              {cta.description}
            </p>

            {/* Industry CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-5 justify-center mb-8 sm:mb-10">
              {cta.industries.map((industry, index) => (
                <button
                  key={index}
                  onClick={() => nav(industry.href)}
                  className={`group inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-r ${industry.gradient} ${industry.hoverGradient} text-white font-bold rounded-2xl transition-all duration-500 transform hover:scale-105 shadow-2xl hover:shadow-3xl text-sm sm:text-base`}
                >
                  {getIcon(industry.icon, { className: "w-5 h-5 sm:w-6 sm:h-6" })}
                  {industry.label}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              ))}
            </div>

            {/* Or contact */}
            <div className="flex items-center justify-center gap-4 mb-8 sm:mb-10">
              <div className="h-px w-16 sm:w-24 bg-white/20" />
              <span className="text-slate-400 text-sm font-medium">or</span>
              <div className="h-px w-16 sm:w-24 bg-white/20" />
            </div>

            <button
              onClick={() => nav("/contact")}
              className="inline-flex items-center gap-2 sm:gap-3 px-8 sm:px-10 py-3.5 sm:py-4 bg-white/10 border border-white/20 text-white font-bold rounded-2xl hover:bg-white/20 transition-all duration-300 transform hover:scale-105 mb-10 sm:mb-12 text-sm sm:text-base"
            >
              <Users className="w-4 h-4 sm:w-5 sm:h-5" />
              {cta.contactButton}
            </button>

            {/* Trust signals */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-slate-400 text-sm sm:text-base font-light">
              {cta.trustPoints.map((point, index) => (
                <div key={index} className="flex items-center gap-2 sm:gap-3">
                  <div className={`w-2 h-2 ${point.color} rounded-full animate-pulse`} />
                  <span>{point.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ParentCTA;